import { useEffect } from "react"

/**
 * Entrance reveals for the landing page band (LANDING-PLAN §5).
 *
 * One IntersectionObserver for the whole page. Elements opt in with
 * [data-reveal]; the observer adds .is-revealed once and unobserves, so nothing
 * is scroll-linked and nothing re-fires.
 *
 * FAIL-VISIBLE BY DESIGN. The hidden state is scoped to .js-reveal-ready, which
 * this component adds to <html> on mount. If JS never runs, is blocked, or
 * throws, no element is ever hidden and the page renders complete. This is the
 * failure mode navbardigital has — its metrics render as 0+ / 0/5 / 0yrs until
 * JS fires — and it is worse than having no animation at all.
 *
 * Nothing above the fold opts in, so LCP is untouched.
 * All motion is disabled under prefers-reduced-motion in CSS.
 */
const ScrollReveal = () => {
  useEffect(() => {
    const root = document.documentElement

    // No IntersectionObserver (or reduced motion): leave everything visible.
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (!("IntersectionObserver" in window) || reduced) return

    root.classList.add("js-reveal-ready")

    const targets = [...document.querySelectorAll("[data-reveal]")]
    if (!targets.length) return

    const remaining = new Set(targets)

    const reveal = el => {
      if (!remaining.has(el)) return
      el.classList.add("is-revealed")
      remaining.delete(el)
      observer.unobserve(el) // fire once
    }

    // threshold 0.15, not 0. At threshold 0 an element reveals the instant a
    // single pixel crosses the viewport edge, so the fade finishes off-screen
    // and the motion is never actually seen. 15% means the element is properly
    // in view before it starts, which is the point of having a reveal at all.
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && reveal(e.target)),
      { threshold: 0.15 }
    )

    targets.forEach(t => observer.observe(t))

    // One-time pass at mount, for elements already ABOVE the viewport — the
    // case where the browser restores scroll position on reload. Those can
    // never satisfy a 15% threshold going forward, so without this they would
    // stay invisible. Runs once inside rAF; there is no ongoing scroll
    // listener, so this costs nothing after mount.
    const revealAlreadyPassed = () => {
      remaining.forEach(el => {
        if (el.getBoundingClientRect().bottom <= 0) reveal(el)
      })
    }
    const raf = window.requestAnimationFrame(revealAlreadyPassed)

    return () => {
      window.cancelAnimationFrame(raf)
      observer.disconnect()
      root.classList.remove("js-reveal-ready")
    }
  }, [])

  return null
}

export default ScrollReveal
